import { useState } from 'react';
import type { FormEvent } from 'react';
import { supabase } from '@/lib/supabase';
// The full brand mark — gear behind the wording — from the owner's logo pack
// (Logos/Allied Automotive Full Logo, added 2026-08-11). The login screen is
// the one place with room to let it breathe.
import fullLogo from '@/assets/allied-full-logo.png';

/**
 * Manager sign-in: email, then a six-digit code from that email.
 *
 * No passwords and no PINs (M3 — PINs are retired), and identity is never
 * Google (M4). An employee losing access to a personal Google account must
 * never mean losing access to their own attendance record.
 *
 * This screen NEVER creates an account. Managers are provisioned by an admin,
 * so `shouldCreateUser: false` is mandatory — see the note on it below.
 */

type Stage = 'email' | 'code';

/**
 * What the error line renders: a sentence written for a service manager, and
 * — only when GoTrue's wording had no mapping — the raw message tucked behind
 * a collapsed "Technical details", so it can be read out to whoever maintains
 * this without ever being the headline.
 */
interface FriendlyError {
  text: string;
  detail?: string;
}

/**
 * Turn Supabase's wording into something a service manager can act on.
 *
 * Deliberately NOT hiding whether an address is registered. That is a mild user
 * enumeration tradeoff, and it is the right one here: ten known employees, an
 * attacker learns nothing useful without the code, and the alternative is
 * showing "check your inbox" to someone who typo'd their address and will then
 * wait for an email that is never coming.
 *
 * Pre-P8 punch list, Tier 2 (error handling): this used to fall through to
 * the RAW message for anything unmapped — so a manager on bad shop wifi saw
 * Chrome's bare "Failed to fetch" with no hint that their connection, not
 * their address, was the problem. Now every path ends in plain language:
 * network failures get a "check your connection" line, and anything still
 * unmapped gets a generic sentence with the raw text behind a disclosure
 * (and in the console) rather than on the page. `stage` matters because the
 * same substrings mean different things per step: "invalid" at the CODE
 * step is a wrong code, but at the EMAIL step it is a malformed address —
 * the old stage-blind matcher would have told that person their CODE was
 * wrong before they had one.
 */
/**
 * GoTrue's stable error CODES (supabase-js v2 `AuthError.code`) for the
 * one-time-code flow — matched exactly, before any wording, because the
 * wording changes between GoTrue releases and the codes do not. Gemini's
 * catch in the 3-party review: several of these ("over_email_send_rate_limit",
 * "email_address_invalid") never contain the words the substring table below
 * looks for.
 */
const CODE_MEANING: Record<string, 'network' | 'rate_limit' | 'bad_code' | 'bad_email' | 'not_allowed'> = {
  over_email_send_rate_limit: 'rate_limit',
  over_request_rate_limit: 'rate_limit',
  over_sms_send_rate_limit: 'rate_limit',
  otp_expired: 'bad_code',
  otp_disabled: 'not_allowed',
  email_address_invalid: 'bad_email',
  validation_failed: 'bad_email',
  signup_disabled: 'not_allowed',
  user_banned: 'not_allowed',
};

function friendlyError(rawMessage: string | undefined, code: string | undefined, stage: Stage): FriendlyError {
  // Defensive: a thrown non-AuthError can arrive with no message at all, and
  // the one thing an error handler must never do is throw itself.
  const message = typeof rawMessage === 'string' ? rawMessage : '';
  const m = message.toLowerCase();
  const meaning = code ? CODE_MEANING[code] : undefined;

  // The request never reached the sign-in service, or the answer never came
  // back. supabase-js surfaces the browser's own fetch wording here: Chrome's
  // "Failed to fetch", Firefox's "NetworkError when attempting to fetch
  // resource.", Safari's "Load failed".
  if (
    m.includes('failed to fetch') ||
    m.includes('networkerror') ||
    m.includes('network error') ||
    m.includes('network request failed') ||
    m.includes('load failed')
  ) {
    return {
      text: 'Could not reach the sign-in service. Check your internet connection and try again.',
    };
  }

  // GoTrue returns "Signups not allowed for otp" for TWO different situations,
  // and gives the client no way to tell them apart:
  //
  //   1. No user with that email address.
  //   2. A user exists but has never confirmed their email — the state an
  //      invited manager sits in until they accept the invitation.
  //
  // Hit for real on 2026-07-28: the account existed, so the first draft of this
  // message ("not set up as a manager") was a confident lie. Since the cause is
  // genuinely ambiguous here, say so and cover both actions.
  if (meaning === 'not_allowed' || m.includes('signups not allowed')) {
    return {
      text:
        'Could not sign you in with that address. If you were recently invited, ' +
        'open the invitation email and accept it first. Otherwise check the ' +
        'spelling, or ask an admin to add you.',
    };
  }
  // The PER-ADDRESS cooldown (hosted smtp_max_frequency = 60s) — the single
  // most common error on this screen, hit by tapping "Email me a code" twice
  // while the first email is still arriving. GoTrue words it "For security
  // purposes, you can only request this after N seconds." with no "rate
  // limit" anywhere in it (Fable + Gemini both caught it). It names the exact
  // wait, so say the number, and say the useful thing: the first code is
  // probably already in the inbox or spam.
  if (
    code === 'over_email_send_rate_limit' ||
    m.includes('security purposes') ||
    m.includes('only request this after')
  ) {
    const wait = /after (\d+) seconds?/i.exec(message);
    return {
      text: wait
        ? `You just requested a code. Wait ${wait[1]} seconds before requesting another — and check your spam folder for the one already sent.`
        : 'A code was already sent to this address a moment ago. Wait about a minute before requesting another, and check your spam folder.',
    };
  }
  // The other throttles: the project-wide hourly email cap and the per-IP
  // request limit. At the code stage the code already in hand stays valid
  // for its full hour — never send anyone off to request another, which
  // would only trip the cooldown above.
  if (
    meaning === 'rate_limit' ||
    m.includes('rate limit') ||
    m.includes('rate_limit') ||
    m.includes('too many')
  ) {
    return {
      text:
        stage === 'email'
          ? 'Too many codes requested. Wait a minute and try again.'
          : 'Too many attempts. Wait a minute and try the same code again.',
    };
  }

  if (stage === 'code') {
    // GoTrue's one message for a WRONG code is "Token has expired or is
    // invalid" — it genuinely cannot tell a typo from an old code, so the
    // old "has expired" wording sent people off to request a new code when
    // they had simply mistyped. Say both.
    if (meaning === 'bad_code' || m.includes('invalid') || m.includes('token')) {
      return {
        text:
          'That code is not right, or it has expired. Check the newest email, or request a new one.',
      };
    }
    if (m.includes('expired')) {
      return { text: 'That code has expired. Request a new one.' };
    }
  } else if (
    meaning === 'bad_email' ||
    m.includes('validate email') ||
    m.includes('invalid format') ||
    m.includes('address is invalid')
  ) {
    return { text: 'That does not look like a valid email address. Check it and try again.' };
  }

  // Anything else is GoTrue wording written for developers. Keep it out of
  // the headline, but keep it reachable.
  console.error('[LoginScreen] unmapped auth error:', message);
  return {
    text:
      stage === 'email'
        ? 'Could not send a code just now. Try again in a moment — if it keeps happening, ask an admin.'
        : 'Could not check that code just now. Try again in a moment, or request a new code.',
    detail: message,
  };
}

export function LoginScreen() {
  const [stage, setStage] = useState<Stage>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  // "Send a new code" from the code stage — its own flag so the Sign in
  // button does not read "Checking…" while a code is merely being re-sent.
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<FriendlyError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function requestCode(e: FormEvent) {
    e.preventDefault();
    const address = email.trim().toLowerCase();
    if (!address) return;
    await sendCode(address, false);
  }

  /**
   * Ask GoTrue to email a code to `address`. Shared by the email form and the
   * code stage's "Send a new code" link — Fable's catch: three of the four
   * code-stage messages ended "request a new code" while the only control on
   * that stage said "Use a different email", the opposite of what a manager
   * with the right address and a lost email wants.
   */
  async function sendCode(address: string, resend: boolean) {
    if (resend) setResending(true);
    else setBusy(true);
    setError(null);
    setNotice(null);

    // supabase-js normally hands transport failures back as `error`, but a
    // thrown one (an aborted request, a mid-flight extension quirk) would
    // otherwise leave the button stuck on "Sending…" forever — so treat a
    // rejection exactly like a returned error.
    let sendError: { message: string; code?: string } | null = null;
    try {
      const res = await supabase.auth.signInWithOtp({
        email: address,
        options: {
          // REQUIRED. supabase-js defaults this to true, and hosted signups are
          // disabled, so without it every attempt fails with "Signups not allowed
          // for otp" — which reads like a broken config rather than the intended
          // behaviour. See P0_CHECKLIST.md §3.4b.
          shouldCreateUser: false,
        },
      });
      sendError = res.error;
    } catch (thrown: unknown) {
      sendError = { message: thrown instanceof Error ? thrown.message : String(thrown) };
    }

    if (resend) setResending(false);
    else setBusy(false);

    if (sendError) {
      setError(friendlyError(sendError.message, sendError.code, 'email'));
      return;
    }

    setEmail(address);
    setStage('code');
    setCode('');
    // GoTrue replaces the outstanding code on every send, so after a re-send
    // the earlier email is dead — worth saying, since it is the one still
    // sitting at the top of the inbox.
    setNotice(
      resend
        ? `A new code was sent to ${address}. Only the newest code works; it is good for one hour.`
        : `Code sent to ${address}. It is good for one hour.`,
    );
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    const token = code.trim();
    if (!token) return;

    setBusy(true);
    setError(null);

    let verifyError: { message: string; code?: string } | null = null;
    try {
      // type: 'email' is the six-digit code flow. Using 'magiclink' here fails in
      // a way that looks like a wrong code.
      const res = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'email',
      });
      verifyError = res.error;
    } catch (thrown: unknown) {
      verifyError = { message: thrown instanceof Error ? thrown.message : String(thrown) };
    }

    setBusy(false);

    if (verifyError) {
      setError(friendlyError(verifyError.message, verifyError.code, 'code'));
      return;
    }
    // On success the session lands and App's auth listener swaps this screen
    // out. Nothing to do here.
  }

  function startOver() {
    setStage('email');
    setCode('');
    setError(null);
    setNotice(null);
  }

  return (
    <main style={S.page}>
      <div style={S.card}>
        <h1 style={S.h1}>
          <img src={fullLogo} alt="Allied Automotive Service" style={S.logo} />
        </h1>
        <p style={S.sub}>Manager sign-in</p>

        {stage === 'email' ? (
          <form onSubmit={requestCode}>
            <label htmlFor="email" style={S.label}>
              Work email
            </label>
            <input
              id="email"
              type="email"
              required
              autoFocus
              autoComplete="email"
              placeholder="you@alliedautomotiveservice.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={S.input}
            />
            <button type="submit" disabled={busy} style={S.button}>
              {busy ? 'Sending…' : 'Email me a code'}
            </button>
          </form>
        ) : (
          <form onSubmit={submitCode}>
            <label htmlFor="code" style={S.label}>
              Six-digit code
            </label>
            <input
              id="code"
              // `text` with a numeric inputMode, not `number` — a number input
              // strips leading zeros, and codes can start with one.
              type="text"
              required
              autoFocus
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              style={{ ...S.input, letterSpacing: '0.4em', fontSize: '1.25rem' }}
            />
            <button type="submit" disabled={busy || resending} style={S.button}>
              {busy ? 'Checking…' : 'Sign in'}
            </button>
            {/* The control the code-stage messages point at. startOver() keeps
                the address on purpose, so "Use a different email" stays the
                OTHER path. Both are disabled while a check is in flight —
                otherwise a late verify error could paint a code-stage
                sentence onto the email form (Fable, state lens). */}
            <button
              type="button"
              onClick={() => void sendCode(email, true)}
              disabled={busy || resending}
              style={S.linkButton}
            >
              {resending ? 'Sending a new code…' : 'Send a new code'}
            </button>
            <button type="button" onClick={startOver} disabled={busy || resending} style={S.linkButton}>
              Use a different email
            </button>
          </form>
        )}

        {/* Pre-P8 punch list, Tier 3: "Code sent to …" was a plain <p> that
            appeared after the stage flipped, so a screen-reader user heard the
            code field take focus and nothing about why. A polite live region
            that is ALWAYS mounted (an empty one at first) — a region created
            already holding text is not reliably announced, only a change to
            an existing one is. Polite, not alert: the focus move already
            interrupts; this just follows it. */}
        <p role="status" aria-live="polite" style={notice ? S.notice : S.noticeEmpty}>
          {notice}
        </p>
        {error && (
          // role="alert" on the sentence ONLY; the disclosure is a sibling, so
          // opening it does not re-announce the whole block (alert is
          // assertive + atomic) and its <summary> is not read as flat prose —
          // the same split ErrorBoundary settled on.
          <div style={S.error}>
            <p role="alert" style={S.errorText}>
              {error.text}
            </p>
            {error.detail && (
              <details style={S.errorDetails}>
                <summary style={S.errorSummary}>Technical details</summary>
                <code style={S.errorCode}>{error.detail}</code>
              </details>
            )}
          </div>
        )}

        {/* P3: employees have no accounts (M2) — their door is the open
            request form, and this line is how someone who landed on the
            login screen finds it. */}
        <p style={S.employeeLine}>
          Requesting time off? <a href="#/request" style={S.employeeLink}>Use the request form</a>
          {' '}— no sign-in needed.
        </p>
      </div>
    </main>
  );
}

const S = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'system-ui, sans-serif',
    padding: '1.5rem',
  },
  card: {
    width: '100%',
    maxWidth: '22rem',
    border: '1px solid #e3e7ee',
    borderRadius: '0.75rem',
    padding: '2rem',
    background: 'white',
    boxShadow: '0 1px 2px rgba(16, 24, 40, 0.05)',
  },
  h1: { margin: '0 0 0.5rem', fontSize: '1.25rem' },
  logo: { display: 'block', height: '110px', width: 'auto', margin: '0 auto' },
  sub: { marginTop: '0.25rem', marginBottom: '1.75rem', color: '#666', fontSize: '0.9rem' },
  label: { display: 'block', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.4rem' },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '0.6rem 0.7rem',
    fontSize: '1rem',
    border: '1px solid #ccc',
    borderRadius: '0.4rem',
    marginBottom: '1rem',
    fontFamily: 'inherit',
  },
  button: {
    width: '100%',
    padding: '0.65rem',
    fontSize: '0.95rem',
    fontWeight: 600,
    color: 'white',
    background: '#1a1a1a',
    border: 0,
    borderRadius: '0.4rem',
    cursor: 'pointer',
  },
  // 44px tall (pre-P8 punch list, Tier 3: was ~29px), both link-buttons.
  // The extra height comes from padding, not margin, so the whole visible
  // row is the tap target; the margin between them shrinks to compensate so
  // the card does not grow by a line.
  linkButton: {
    width: '100%',
    marginTop: '0.25rem',
    minHeight: '2.75rem',
    padding: '0.4rem',
    fontSize: '0.85rem',
    color: '#666',
    background: 'none',
    border: 0,
    cursor: 'pointer',
    textDecoration: 'underline',
    fontFamily: 'inherit',
  },
  notice: { marginTop: '1.25rem', marginBottom: 0, fontSize: '0.85rem', color: '#555' },
  noticeEmpty: { margin: 0 },
  error: { marginTop: '1.25rem', fontSize: '0.85rem', color: '#b00020', lineHeight: 1.5 },
  errorText: { margin: 0 },
  errorDetails: { marginTop: '0.25rem', color: '#555' },
  // Vertical padding lifts the <summary> to a ~44px target, same as
  // ErrorBoundary's (and, like there, not display:flex — that would drop the
  // native disclosure triangle).
  errorSummary: { cursor: 'pointer', padding: '0.85rem 0' },
  errorCode: {
    display: 'block',
    marginTop: '0.35rem',
    padding: '0.4rem 0.55rem',
    borderRadius: '0.35rem',
    background: '#f4f5f7',
    fontSize: '0.78rem',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
  },
  employeeLine: {
    marginTop: '1.5rem',
    marginBottom: 0,
    paddingTop: '1rem',
    borderTop: '1px solid #f0f0f0',
    fontSize: '0.8rem',
    // #666 (5.7:1 on white), not #888 (3.2:1) — pre-P8 punch list, Tier 3 contrast sweep.
    color: '#666',
  },
  employeeLink: { color: '#0b46c4' },
} satisfies Record<string, React.CSSProperties>;
